import React, { useState, useMemo } from 'react';
import { Box, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LoopIcon from '@mui/icons-material/Loop';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';

interface Props {
  status: string;
  isDisabled: boolean;
  onChange: (newStatus: string) => void;
}

const options = [
  { label: 'In Progress', icon: <LoopIcon fontSize="small" /> },
  { label: 'On Hold', icon: <PauseCircleIcon fontSize="small" /> }
];

// Add all possible status labels here
const allStatusLabels = ['To Do', 'In Progress', 'On Hold', 'Completed'];

const STATUS_SX: Record<string, any> = {
  'In Progress': { bgcolor: '#EAF2FF', color: '#2F5FAA' },
  'On Hold': { bgcolor: '#FFF2D9', color: '#8A5C00' }
};
const getStyle = (s: string) =>
  STATUS_SX[s] ?? { bgcolor: '#ECEFF3', color: '#39424E' };

const StatusDropdown: React.FC<Props> = ({ status, isDisabled, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const pick = (s: string) => {
    onChange(s);
    closeMenu();
  };

  const filteredOptions = options.filter((opt) => opt.label !== status);

  // Calculate max label length and set chip width accordingly
  const maxLabelLength = useMemo(
    () => Math.max(...allStatusLabels.map((l) => l.length)),
    []
  );
  // Estimate width: 8px per character + padding
  const chipWidth = maxLabelLength * 8 + 32;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: chipWidth + 40 }} // Chip width + space for icon
    >
      <Chip
        label={status}
        sx={{
          ...getStyle(status),
          height: 28,
          borderRadius: '999px',
          fontWeight: 600,
          px: 1,
          width: chipWidth,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiChip-label': {
            px: 0.75,
            fontSize: 12,
            width: '100%',
            textAlign: 'center'
          }
        }}
      />
      <IconButton size="small" onClick={openMenu} aria-label="change status">
        <ArrowDropDownIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {filteredOptions.map((opt) => (
          <MenuItem
            key={opt.label}
            onClick={() => pick(opt.label)}
            disabled={isDisabled}
            sx={{ '&:hover': { opacity: 0.9 } }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {opt.icon}
              {opt.label}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default StatusDropdown;
